<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  reports: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const sortedReports = computed(() => {
  return [...props.reports].sort(
    (a, b) => b.waiting_minutes - a.waiting_minutes
  );
});

const formatWaitingTime = minutes => {
  if (minutes < 60) {
    return `${minutes} ${t('COMPLIANCE.MINUTES')}`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMins = minutes % 60;
  if (remainingMins === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${remainingMins}m`;
};

const getRowClass = report => {
  if (report.exceeds_threshold) {
    return 'bg-r-50 dark:bg-r-900/20';
  }
  return '';
};
</script>

<template>
  <div class="overflow-hidden border rounded-lg border-n-weak">
    <table class="min-w-full divide-y divide-n-weak">
      <thead class="bg-n-solid-2">
        <tr>
          <th
            class="px-4 py-3 text-xs font-medium tracking-wider text-left uppercase text-n-slate-11"
          >
            {{ t('COMPLIANCE.TABLE.CONVERSATION') }}
          </th>
          <th
            class="px-4 py-3 text-xs font-medium tracking-wider text-left uppercase text-n-slate-11"
          >
            {{ t('COMPLIANCE.TABLE.CONTACT') }}
          </th>
          <th
            class="px-4 py-3 text-xs font-medium tracking-wider text-left uppercase text-n-slate-11"
          >
            {{ t('COMPLIANCE.TABLE.AGENT') }}
          </th>
          <th
            class="px-4 py-3 text-xs font-medium tracking-wider text-left uppercase text-n-slate-11"
          >
            {{ t('COMPLIANCE.TABLE.INBOX') }}
          </th>
          <th
            class="px-4 py-3 text-xs font-medium tracking-wider text-left uppercase text-n-slate-11"
          >
            {{ t('COMPLIANCE.TABLE.WAITING_TIME') }}
          </th>
        </tr>
      </thead>
      <tbody class="divide-y divide-n-weak bg-n-solid-1">
        <template v-if="isLoading">
          <tr v-for="i in 5" :key="i">
            <td v-for="j in 5" :key="j" class="px-4 py-4">
              <div class="w-24 h-4 rounded bg-n-alpha-2 animate-pulse" />
            </td>
          </tr>
        </template>
        <template v-else-if="sortedReports.length === 0">
          <tr>
            <td colspan="5" class="px-4 py-8 text-center text-n-slate-11">
              {{ t('COMPLIANCE.TABLE.NO_DATA') }}
            </td>
          </tr>
        </template>
        <template v-else>
          <tr
            v-for="report in sortedReports"
            :key="report.id"
            :class="getRowClass(report)"
          >
            <td class="px-4 py-4 text-sm text-n-slate-12">
              <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
              #{{ report.display_id }}
            </td>
            <td class="px-4 py-4 text-sm text-n-slate-12">
              {{ report.contact_name || '-' }}
            </td>
            <td class="px-4 py-4 text-sm text-n-slate-12">
              <span :class="report.assignee ? '' : 'text-n-slate-10 italic'">
                {{ report.assignee || t('COMPLIANCE.UNASSIGNED') }}
              </span>
            </td>
            <td class="px-4 py-4 text-sm text-n-slate-12">
              {{ report.inbox || '-' }}
            </td>
            <td class="px-4 py-4 text-sm">
              <span
                class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full"
                :class="
                  report.exceeds_threshold
                    ? 'bg-r-100 text-r-700 dark:bg-r-900 dark:text-r-300'
                    : 'bg-n-alpha-2 text-n-slate-11'
                "
              >
                <span
                  v-if="report.exceeds_threshold"
                  class="w-1.5 h-1.5 rounded-full bg-r-500 animate-pulse"
                />
                {{ formatWaitingTime(report.waiting_minutes) }}
              </span>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>
